// src/pages/admin/TabList.js
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Button } from "@mui/material";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import { Box } from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, ModalClose, Sheet, Typography } from "@mui/joy";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

export default function TabList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [tabs, setTabs] = useState([]);
  const [callApi, setCallApi] = useState(false);
  const [warning, setWarning] = useState({ text: "", type: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);

  const fetchTabs = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}common/tabs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTabs(res.data);
    } catch (e) {
      alert("Session expired");
      localStorage.clear();
      navigate("/login");
    }
  };

  // toggle active
  const changeStatus = async (id) => {
    const t = tabs.find((x) => x._id === id);
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}common/tabs/${id}`,
        { active: !t.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi((prev) => !prev);
    } catch (e) {
      alert("Failed to change status");
    }
  };

  // delete
  const deleteTab = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BASE_URL}common/tabs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCallApi((prev) => !prev);
    } catch (e) {
      alert("Failed to delete tab");
    }
  };

  useEffect(() => {
    fetchTabs();
  }, [callApi]);

  const columns = [
    { field: "s_no", headerName: "S.no", width: 80 },
    { field: "storehouse", headerName: "Storehouse", width: 150 },
    { field: "index", headerName: "Index", width: 100 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "active", headerName: "Active", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const tab = tabs.find((t) => t._id === params.row.id);
        if (!tab) return null;

        return (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {/* Edit */}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => navigate(`/edit-tab/${tab._id}`)}
            >
              <i className="fas fa-edit" />
            </Button>

            {/* Toggle Active/Inactive */}
            <Button
              size="small"
              variant="contained"
              color={tab.active ? "success" : "error"}
              onClick={() => {
                setSelectedId(tab._id);
                setWarning({
                  text: `Change status to ${tab.active ? "Inactive" : "Active"}?`,
                  type: "status",
                });
                setOpenWarning(true);
              }}
            >
              {tab.active ? "Active" : "Inactive"}
            </Button>

            {/* Delete */}
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => {
                setSelectedId(tab._id);
                setWarning({ text: "Delete this tab?", type: "delete" });
                setOpenWarning(true);
              }}
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
        );
      },
    },
  ];

  const rows = tabs.map((t, idx) => ({
    id: t._id,
    s_no: idx + 1,
    storehouse: t.storehouse.code,
    index: t.index,
    name: t.name,
    active: t.active ? "Yes" : "No",
  }));

  return (
    <PageLayout>
      <Grid container spacing={2}>
        <Grid
          size={{ xs: 12 }}
          className="d-flex justify-content-between align-items-center"
        >
          <Breadcrumb title="Tab List" />
          <Link to="/add-tab">
            <Button variant="contained" color="primary">Add Tab</Button>
          </Link>
        </Grid>

        <Grid size={{ xs: 12 }} className="mt-3">
          <Box className="mc-card">
            <CardHeader title="Tab List" />
            <div style={{ height: 600, width: "100%", marginTop: "1rem" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                autoHeight={false}
              />
            </div>
          </Box>
        </Grid>

        {/* Confirmation Modal */}
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={openWarning}
          onClose={() => setOpenWarning(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "1000",
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              width: "30%",
              borderRadius: "md",
              p: 3,
              boxShadow: "lg",
              bgcolor: "#fff",
            }}
          >
            <ModalClose
              variant="outlined"
              sx={{
                top: "calc(-1/4 * var(--IconButton-size))",
                right: "calc(-1/4 * var(--IconButton-size))",
                boxShadow: "0 2px 12px 0 rgba(0 0 0 / 0.2)",
                borderRadius: "50%",
                bgcolor: "#eee",
              }}
            />
            <Typography component="h2" fontSize="1.25em" mb={5}>
              {warning.text}
            </Typography>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "2rem",
              }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={(e) => {
                  e.preventDefault();
                  if (warning.type === "status") changeStatus(selectedId);
                  else deleteTab(selectedId);
                  setOpenWarning(false);
                }}
                style={{ width: "8rem", fontSize: "0.8rem" }}
              >
                Yes
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenWarning(false)}
                style={{ width: "8rem", fontSize: "0.8rem" }}
              >
                No
              </Button>
            </div>
          </Sheet>
        </Modal>
      </Grid>
    </PageLayout>
  );
}
